import React, { memo, useEffect } from 'react';
import { useParams } from 'react-router-dom/';
import { formats, validators, dates } from 'investira.sdk';
import services from '../services';

// Context
const EmpresasContext = React.createContext();

const initialState = {
  pessoa: {},
  perfil: {},
  showPrecise: true,
  isPageLoading: true,
  variantChart: null,
  historico: {
    pl: [],
    cs: [],
    saldos: []
  },
  config: {
    consolidacao_tipo: 1
  },
  empresas: [],
  ativo_avaliacao: {}
};

// Reducers
const reducers = (state = initialState, action) => {
  if (action.type === 'PESSOA_CHANGED') {
    return {
      ...state,
      pessoa: action.payload
    };
  }

  if (action.type === 'EMPRESAS_CHANGED') {
    return {
      ...state,
      empresas: action.payload
    };
  }

  if (action.type === 'PERFIL_CHANGED') {
    return {
      ...state,
      perfil: action.payload
    };
  }

  if (action.type === 'SHOW_PRECISE_CHANGED') {
    return {
      ...state,
      showPrecise: action.payload
    };
  }

  if (action.type === 'IS_PAGE_LOADING_CHANGED') {
    return {
      ...state,
      isPageLoading: action.payload
    };
  }

  if (action.type === 'IS_AVALIACAO_LOADING_CHANGED') {
    return {
      ...state,
      isAvaliacaoLoading: action.payload
    };
  }

  if (action.type === 'VARIANT_CHART_CHANGED') {
    return {
      ...state,
      variantChart: action.payload
    };
  }

  if (action.type === 'HISTORICO_CHANGED') {
    return {
      ...state,
      historico: {
        ...state.historico,
        ...action.payload
      }
    };
  }

  if (action.type === 'CONFIG_CHANGED') {
    return {
      ...state,
      config: {
        ...state.config,
        ...action.payload
      }
    };
  }

  if (action.type === 'AVALIACAO_CHANGED') {
    return {
      ...state,
      ativo_avaliacao: action.payload
    };
  }
};

// Dispatchs
const acShowPreciseChanged = pBool => ({
  type: 'SHOW_PRECISE_CHANGED',
  payload: pBool
});

const acPessoaChanged = pObjPessoa => ({
  type: 'PESSOA_CHANGED',
  payload: pObjPessoa
});

const acEmpresasChanged = pObjEmpresas => ({
  type: 'EMPRESAS_CHANGED',
  payload: pObjEmpresas
});

const acPerfilChanged = pObjPessoa => ({
  type: 'PERFIL_CHANGED',
  payload: pObjPessoa
});

const acIsPageLoadingChanged = pBool => ({
  type: 'IS_PAGE_LOADING_CHANGED',
  payload: pBool
});

const acIsAvaliacaoLoadingChanged = pBool => ({
  type: 'IS_AVALIACAO_LOADING_CHANGED',
  payload: pBool
});

const acVariantChartChanged = pStr => ({
  type: 'VARIANT_CHART_CHANGED',
  payload: pStr
});

const acHistoricoChanged = pObj => ({
  type: 'HISTORICO_CHANGED',
  payload: pObj
});

const acConfigChanged = pObj => ({
  type: 'CONFIG_CHANGED',
  payload: pObj
});

const acAvaliacaoChanged = pObjAvaliacao => ({
  type: 'AVALIACAO_CHANGED',
  payload: pObjAvaliacao
});

// Custom Hook
export const useEmpresasContext = () => React.useContext(EmpresasContext);

export const EmpresasProvider = memo(({ children }) => {
  const TODAY = new Date();
  const DATA_FIM = dates.toSqlDate(dates.toSqlDate(TODAY));
  const DATA_INICIO = dates.addYears(dates.toSqlDate(TODAY), -10);

  const params = useParams();
  const [state, dispatch] = React.useReducer(reducers, initialState);

  const formatNumber = (
    pValue = null,
    pShowPrecise = true,
    pShowCurrency = true,
    pDecimal = 2,
    pBase = 'mi'
  ) => {
    if (validators.isNull(pValue) || isNaN(pValue)) {
      return '';
    }

    if (!pShowPrecise) {
      return formats.formatNumber(pValue, pDecimal, true, pShowCurrency);
    }

    return formats.friendlyNumber(pValue || 0, pDecimal, pShowCurrency, pBase);
  };

  const handleShowPreciseChange = pValue => {
    dispatch(acShowPreciseChanged(pValue));
  };

  const handlePessoaChange = pValue => {
    dispatch(acPessoaChanged(pValue));
  };

  const handleEmpresasChange = pValue => {
    dispatch(acEmpresasChanged(pValue));
  };

  const handlePerfilChange = pValue => {
    dispatch(acPerfilChanged(pValue));
  };

  const handleChartVariantChange = pValue => {
    dispatch(acVariantChartChanged(pValue));
  };

  const handleHistoricoChange = pObj => {
    dispatch(acHistoricoChanged(pObj));
  };

  const handleConfigChange = pObj => {
    dispatch(acConfigChanged(pObj));
  };

  const handleAvaliacaoChange = pValue => {
    dispatch(acAvaliacaoChanged(pValue));
  };

  useEffect(() => {
    if (params.pessoa_id) {
      services.pessoas.list(
        { pessoa_id: params.pessoa_id },
        rRes => {
          handlePessoaChange(rRes.data[0]);
          services.pessoas.perfil(
            { pessoa_id: params.pessoa_id },
            rRes => {
              handlePerfilChange(rRes.data);
            },
            rErr => {
              console.log(rErr);
            },
            () => {
              dispatch(acIsPageLoadingChanged(false));
            }
          );
        },
        rErr => {
          console.log(rErr);
        }
      );
      services.pessoas.avaliacao(
        {
          pessoa_id: params.pessoa_id
          // data: dates.toSqlDate(dates.endOf('month', TODAY))
          // data: TODAY
        },
        rRes => {
          const xAvaliacao = rRes.data[0];
          console.log('xAvaliacao', xAvaliacao);
          handleAvaliacaoChange(xAvaliacao);
        },
        rErr => {
          console.log(rErr);
        },
        () => {
          dispatch(acIsPageLoadingChanged(false));
        }
      );
    }
  }, [params.pessoa_id]);

  useEffect(() => {
    const xParams = {
      data: null,
      data_inicio: DATA_INICIO,
      data_fim: DATA_FIM,
      pessoa_id: params.pessoa_id,
      sort: 'data ASC'
      // consolidacao_tipo: state.config.consolidacao_tipo,
    };

    services.pessoas.pl(xParams, rRes => {
      handleHistoricoChange({ pl: rRes.data });
    });

    services.pessoas.cs(xParams, rRes => {
      handleHistoricoChange({ cs: rRes.data });
    });

    services.pessoas.saldos({ ...xParams, conta: '3.08.00.00.00.00.00' }, rRes => {
      handleHistoricoChange({ saldos: rRes.data });
    });
  }, [params.pessoa_id, state.config.consolidacao_tipo]);

  function readEmpresasSemelhantes(pParams, pResolve, pReject, pFinally) {
    if (!pParams) {
      pParams = {
        metrica: 'pl',
        faixa: 'maiores'
      };
    }
    services.pessoas.listSemelhantes(
      pParams,
      rRes => {
        handleEmpresasChange(rRes.data);
        pResolve && pResolve(rRes);
      },
      rErr => {
        pReject && pReject(rErr);
      },
      () => {
        pFinally && pFinally();
      }
    );
  }

  return (
    <EmpresasContext.Provider
      value={{
        state,
        actions: {
          handleShowPreciseChange,
          handlePessoaChange,
          handleChartVariantChange,
          formatNumber,
          handleConfigChange,
          readEmpresasSemelhantes
        }
      }}>
      {children}
    </EmpresasContext.Provider>
  );
});

export default EmpresasProvider;
